import { useState } from 'react';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface OnboardingGuideProps {
  totalBookings: number;
  onAddBooking?: () => void;
}

const TRACK_EMAIL = 'track@perchtravel.com';

/**
 * Onboarding guide that phases out based on booking count.
 *
 * Phase-out rules:
 *   0 bookings  → hidden (EmptyDashboard handles this state)
 *   1-2 bookings → expanded by default
 *   3-4 bookings → collapsed by default
 *   5+ bookings  → hidden entirely (email discoverable in Add Booking modal)
 */
export function OnboardingGuide({ totalBookings, onAddBooking }: OnboardingGuideProps) {
  const { copied, copy: copyEmail } = useCopyToClipboard(TRACK_EMAIL);

  // Phase-out: hidden at 0 (empty state) or 5+ (power user)
  if (totalBookings === 0 || totalBookings >= 5) return null;

  // Auto-expand for new users (1-2 bookings), collapse for 3-4
  const autoExpand = totalBookings < 3;
  const [expanded, setExpanded] = useState(autoExpand);

  return (
    <div className="rounded-2xl border border-stone-200/60 dark:border-stone-700/40 bg-stone-50/50 dark:bg-stone-800/30 overflow-hidden transition-[transform,box-shadow,border-color] duration-300">
      {/* Always visible header row */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-stone-100/50 dark:hover:bg-stone-700/30 transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-stone-200/80 dark:bg-stone-700/80 flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-stone-500 dark:text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-mono text-sm text-stone-900 dark:text-stone-100">
            {TRACK_EMAIL}
          </div>
          <div className="text-xs text-stone-500 dark:text-stone-400">
            Forward your confirmation emails here
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              copyEmail();
            }}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-[background-color,color] ${
              copied
                ? 'bg-emerald-500 text-white'
                : 'bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-300 hover:bg-stone-300 dark:hover:bg-stone-600'
            }`}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>

          <svg
            className={`w-4 h-4 text-stone-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expandable steps section */}
      <div
        className={`overflow-hidden transition-colors duration-300 ease-out ${
          expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-4 pt-2 border-t border-stone-200/50 dark:border-stone-700/30">
          <div className="flex gap-6 text-sm">
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-stone-300 dark:bg-stone-600 flex items-center justify-center text-xs font-medium text-stone-600 dark:text-stone-300">1</span>
              <span className="text-stone-600 dark:text-stone-400">Book anywhere</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-stone-300 dark:bg-stone-600 flex items-center justify-center text-xs font-medium text-stone-600 dark:text-stone-300">2</span>
              <span className="text-stone-600 dark:text-stone-400">Forward email</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-stone-300 dark:bg-stone-600 flex items-center justify-center text-xs font-medium text-stone-600 dark:text-stone-300">3</span>
              <span className="text-stone-600 dark:text-stone-400">We watch & rebook</span>
            </div>
          </div>

          {onAddBooking && totalBookings < 3 && (
            <button
              onClick={onAddBooking}
              className="mt-3 text-xs text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300"
            >
              or add manually →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
